import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Typography, Divider, Button } from "@mui/material";

import { getEntityProfileRoute } from "../../../../../../routers/routeUtils";
import { useSingleMediaUpload } from "../../../../../../api/v1/mutations/useMediaUpload";

import { useEntityDetails } from "../../../../../Entity/api/queries/useEntityDetails";
import { useUpdateEntityProfile } from "../../../../../Entity/api/mutations/useUpdateEntityProfile";

import { entityProfileEditSchema } from "../../../../schemas/editEntityProfile";
import { EditEntityProfileData } from "../../../../types/editEntityProfile-type";

import {
  EntityNameField,
  EntityLevelField,
  EntityTypeField,
  CountryField,
  EstablishedYearField,
  EntityAboutField,
  EntityLogoField,
  PlayingLevelField,
} from "./fields";

interface ExtendedEntityProfileData extends EditEntityProfileData {
  mediaFile?: File;
}

const GeneralInfoTab = () => {
  const { t } = useTranslation("profiles", { keyPrefix: "entityProfileEdit" });
  const navigate = useNavigate();
  const { entityId } = useParams();

  const { data: entityData } = useEntityDetails(entityId!);
  const { mutate: uploadMedia } = useSingleMediaUpload();
  const { mutate: updateEntityProfile } = useUpdateEntityProfile();

  const methods = useForm<ExtendedEntityProfileData>({
    resolver: zodResolver(entityProfileEditSchema),
    values: entityData
      ? {
          about: entityData.about,
          mediaUrl: entityData.mediaUrl,
          // entityName: entityData.name,
          level: entityData.level,
          type: entityData.type,
          countryId: entityData.country?.id,
          foundedYear: entityData.foundedYear || 0,
          listEntry: entityData?.listEntry?.map((level: any) => level.id) || [],
          name_ar: entityData.name_ar ?? "",
          name_en: entityData.name_en ?? "",
        }
      : undefined,
    mode: "all",
  });

  const onSubmit = async (data: ExtendedEntityProfileData) => {
    const hasMediaChanged = data.mediaFile && data.mediaFile.name !== entityData?.mediaUrl;

    if (hasMediaChanged && data.mediaFile) {
      uploadMedia(
        { file: data.mediaFile },
        {
          onSuccess: (response) => {
            updateEntityProfile(
              {
                entityId: Number(entityId),
                about: data.about || "",
                mediaUrl: response.fileName,
                // name: data.entityName,
                level: data.level,
                type: data.type,
                countryId: data.countryId,
                foundedYear: data.foundedYear,
                listEntry: data.listEntry,
                name_ar: data.name_ar,
                name_en: data.name_en,
              },
              {
                onSuccess: () => {
                  navigate(getEntityProfileRoute(entityId!));
                },
              }
            );
          },
        }
      );
    } else {
      updateEntityProfile(
        {
          entityId: Number(entityId),
          about: data.about ?? "",
          mediaUrl: entityData?.mediaUrl ?? "",
          // name: data.entityName,
          level: data.level,
          type: data.type,
          countryId: data.countryId,
          foundedYear: data.foundedYear,
          listEntry: data.listEntry,
          name_ar: data.name_ar,
          name_en: data.name_en,
        },
        {
          onSuccess: () => {
            navigate(getEntityProfileRoute(entityId!));
          },
        }
      );
    }
  };

  const handleCancel = () => {
    methods.reset();
    navigate(getEntityProfileRoute(entityId!));
  };

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={methods.handleSubmit(onSubmit)}
        sx={{
          borderRadius: "8px",
          border: "1px solid #EAECF0",
          background: "#FFF",
          padding: "24px 16px",
        }}
      >
        <Typography
          sx={{
            color: "var(--Dark---1000, #111)",
            fontFamily: "Montserrat",
            fontSize: "16px",
            fontStyle: "normal",
            fontWeight: 700,
            lineHeight: "24px",
            mb: "24px",
          }}
        >
          {t("basicInfo")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <EntityLogoField />
          <EntityNameField />
          <EntityLevelField />
          <EntityTypeField />
          <CountryField />
          <EstablishedYearField />
          <PlayingLevelField />
          <EntityAboutField />

          <Divider
            orientation="horizontal"
            sx={{
              mx: "-16px",
            }}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "16px" }}>
            <Button variant="outlined-sm" type="button" onClick={handleCancel}>
              {t("cancel")}
            </Button>
            <Button variant="primary-sm" type="submit">
              {t("save")}
            </Button>
          </Box>
        </Box>
      </Box>
    </FormProvider>
  );
};

export default GeneralInfoTab;
